import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SearchExecutionContext = createContext();

export const useSearchExecution = () => {
  const context = useContext(SearchExecutionContext);
  if (!context) {
    throw new Error('useSearchExecution must be used within a SearchExecutionProvider');
  }
  return context;
};

export const SearchExecutionProvider = ({ children }) => {
  const [executionState, setExecutionState] = useState(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem('searchExecutionState');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Check if the execution is still valid (not older than 1 hour)
        const startTime = new Date(parsed.startTime);
        const now = new Date();
        const hoursSinceStart = (now - startTime) / (1000 * 60 * 60);
        
        if (hoursSinceStart < 1 && parsed.status === 'running') {
          return parsed;
        }
      } catch (e) {
        console.error('Failed to parse stored execution state:', e);
      }
    }
    return {
      isRunning: false,
      batchId: null,
      batchName: null,
      status: null,
      startTime: null
    };
  });

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    if (executionState.isRunning) {
      localStorage.setItem('searchExecutionState', JSON.stringify(executionState));
    } else {
      localStorage.removeItem('searchExecutionState');
    }
  }, [executionState]);

  const startExecution = useCallback((batchId, batchName) => {
    // Validate batchId is a valid UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!batchId || !uuidRegex.test(batchId)) {
      console.error('Invalid batch ID provided to startExecution:', batchId);
      return;
    }

    const newState = {
      isRunning: true,
      batchId,
      batchName,
      status: 'running',
      startTime: new Date().toISOString()
    };
    setExecutionState(newState);

    // Also store in sessionStorage for quick access
    sessionStorage.setItem('currentBatchId', batchId);
  }, []);

  const updateExecutionStatus = useCallback((status) => {
    setExecutionState(prev => ({
      ...prev,
      status
    }));
  }, []);

  const completeExecution = useCallback((status = 'completed') => {
    setExecutionState({
      isRunning: false,
      batchId: null,
      batchName: null,
      status: null,
      startTime: null
    });
    
    // Clear sessionStorage
    sessionStorage.removeItem('currentBatchId');
  }, []);

  const isExecuting = useCallback(() => {
    return executionState.isRunning;
  }, [executionState.isRunning]);

  const getCurrentBatchId = useCallback(() => {
    return executionState.batchId;
  }, [executionState.batchId]);

  // Check for stale executions on mount: first by age, then by asking the
  // server. If the batch was deleted (or never existed), the polling loop in
  // QueryTemplateManager would otherwise 404 forever.
  useEffect(() => {
    if (!executionState.isRunning || !executionState.batchId) return;

    const startTime = new Date(executionState.startTime);
    const hoursSinceStart = (new Date() - startTime) / (1000 * 60 * 60);
    if (hoursSinceStart >= 1) {
      console.log('Clearing stale execution state (age)');
      completeExecution('timeout');
      return;
    }

    // Verify the batch still exists server-side
    let cancelled = false;
    (async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
        const token = localStorage.getItem('accessToken');
        if (!token) return;
        const res = await fetch(`${apiUrl}/search-execution/batches/${executionState.batchId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.status === 404) {
          console.log('Clearing stale execution state (batch deleted)');
          completeExecution('stale');
        } else if (res.ok) {
          const data = await res.json();
          const status = data?.batch?.status;
          if (status && status !== 'running' && status !== 'pending') {
            // Server says it already finished — sync local state
            completeExecution(status);
          }
        }
      } catch {
        // Network blip — leave state alone; polling will recover.
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for storage events to sync across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'searchExecutionState') {
        if (e.newValue) {
          try {
            const newState = JSON.parse(e.newValue);
            setExecutionState(newState);
          } catch (error) {
            console.error('Failed to parse storage event:', error);
          }
        } else {
          // Storage was cleared
          setExecutionState({
            isRunning: false,
            batchId: null,
            batchName: null,
            status: null,
            startTime: null
          });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const value = {
    executionState,
    startExecution,
    updateExecutionStatus,
    completeExecution,
    isExecuting,
    getCurrentBatchId
  };

  return (
    <SearchExecutionContext.Provider value={value}>
      {children}
    </SearchExecutionContext.Provider>
  );
};